/**
 * Pattern Gallery - Browse, filter, and CRUD pattern functions.
 * In-module gallery panel for the Trinity System playground.
 */

import type { PatternData, PatternCategory } from './types';
import { toPixelCentered, orientation } from './tri-cube';

const SQRT3 = Math.sqrt(3);

const STYLES = {
  bg: '#1e293b',
  bgDark: '#0f172a',
  text: '#e2e8f0',
  muted: '#94a3b8',
  accent: '#1BB1E7',
  border: '#334155',
  font: 'system-ui, sans-serif',
} as const;

const CATEGORIES: Array<PatternCategory | 'all'> = ['all', 'icon', 'basic', 'image', 'logo'];

export class PatternGallery {
  private container: HTMLElement;
  private patterns: PatternData[] = [];
  private activeCategory: PatternCategory | 'all' = 'all';
  private onSelect: ((pattern: PatternData) => void) | null = null;
  private onApplyFunction: ((functionBody: string) => void) | null = null;
  private apiBase: string;

  constructor(container: HTMLElement, apiBase?: string) {
    this.container = container;
    const host = typeof window !== 'undefined' ? window.location.hostname : '';
    const adminOrigin = host.includes('trinity.agency')
      ? 'https://admin.trinity.agency'
      : 'http://localhost:3001';
    this.apiBase = apiBase || `${adminOrigin}/api/playground/triangle-functions`;
    this.render();
  }

  setOnSelect(cb: (pattern: PatternData) => void): void { this.onSelect = cb; }
  setOnApplyFunction(cb: (functionBody: string) => void): void { this.onApplyFunction = cb; }

  async fetchPatterns(): Promise<void> {
    try {
      const res = await fetch(this.apiBase);
      if (!res.ok) { console.error('Fetch patterns failed:', res.status); return; }
      const data = await res.json();
      this.patterns = (data.functions || []).map((f: Record<string, unknown>) => ({
        id: f.id ?? f.readableId,
        readableId: f.readableId ?? f.readable_id,
        name: f.name,
        description: f.description ?? '',
        category: f.category ?? 'basic',
        functionBody: f.functionBody ?? f.function_body ?? null,
        triangles: f.triangles ?? [],
        originA: f.originA ?? f.origin_a ?? 0,
        originB: f.originB ?? f.origin_b ?? 0,
        originC: f.originC ?? f.origin_c ?? 0,
        width: f.width,
        height: f.height,
        createdAt: f.createdAt ?? f.created_at,
        updatedAt: f.updatedAt ?? f.updated_at,
      } as PatternData));
    } catch { /* keep existing patterns on failure */ }
    this.render();
  }

  async savePattern(pattern: PatternData): Promise<PatternData | null> {
    const count = pattern.triangles.length || 1;
    const body = {
      name: pattern.name,
      sourceFilename: pattern.name,
      width: 100,
      height: 100,
      description: pattern.description,
      category: pattern.category,
      functionBody: pattern.functionBody,
      originA: pattern.originA,
      originB: pattern.originB,
      originC: pattern.originC,
      variants: [
        { size: 'S', triangleCount: count, sideLength: 10, triangles: pattern.triangles },
        { size: 'M', triangleCount: count, sideLength: 20, triangles: pattern.triangles },
        { size: 'L', triangleCount: count, sideLength: 30, triangles: pattern.triangles },
      ],
    };
    try {
      const res = await fetch(this.apiBase, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const text = await res.text();
        console.error('Save failed:', res.status, text);
        return null;
      }
      const created = await res.json();
      await this.fetchPatterns();
      return created;
    } catch (err) {
      console.error('Save failed:', err);
      return null;
    }
  }

  async updatePattern(id: string, updates: Partial<PatternData>): Promise<void> {
    await fetch(`${this.apiBase}/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updates),
    });
    await this.fetchPatterns();
  }

  async deletePattern(id: string): Promise<void> {
    await fetch(`${this.apiBase}/${id}`, { method: 'DELETE' });
    await this.fetchPatterns();
  }

  setCategory(category: PatternCategory | 'all'): void {
    this.activeCategory = category;
    this.render();
  }

  private getFilteredPatterns(): PatternData[] {
    if (this.activeCategory === 'all') return this.patterns;
    return this.patterns.filter((p) => p.category === this.activeCategory);
  }

  private render(): void {
    this.container.innerHTML = '';
    this.container.style.cssText = `background:${STYLES.bg};color:${STYLES.text};font-family:${STYLES.font};font-size:12px;display:flex;flex-direction:column;gap:8px;padding:8px;border-radius:6px;border:1px solid ${STYLES.border};`;
    this.container.appendChild(this.renderHeader());
    this.container.appendChild(this.renderCategoryFilter());
    const filtered = this.getFilteredPatterns();
    this.container.appendChild(filtered.length ? this.renderPatternGrid() : this.renderEmptyState());
    this.container.appendChild(this.renderFunctionEditor());
  }

  private renderHeader(): HTMLElement {
    const div = document.createElement('div');
    div.style.cssText = 'display:flex;justify-content:space-between;align-items:center;';
    const title = document.createElement('span');
    title.textContent = 'Pattern Gallery';
    title.style.cssText = `font-size:14px;font-weight:600;color:${STYLES.text};`;
    const btn = document.createElement('button');
    btn.textContent = '\u21BB';
    btn.title = 'Refresh';
    btn.style.cssText = `background:none;border:1px solid ${STYLES.border};color:${STYLES.muted};cursor:pointer;border-radius:4px;padding:2px 6px;font-size:14px;`;
    btn.addEventListener('click', () => this.fetchPatterns());
    div.append(title, btn);
    return div;
  }

  private renderCategoryFilter(): HTMLElement {
    const row = document.createElement('div');
    row.style.cssText = 'display:flex;gap:4px;flex-wrap:wrap;';
    for (const cat of CATEGORIES) {
      const btn = document.createElement('button');
      btn.textContent = cat.charAt(0).toUpperCase() + cat.slice(1);
      const active = cat === this.activeCategory;
      btn.style.cssText = `padding:2px 8px;border-radius:4px;border:1px solid ${active ? STYLES.accent : STYLES.border};background:${active ? STYLES.accent : 'transparent'};color:${active ? '#fff' : STYLES.muted};cursor:pointer;font-size:11px;font-family:${STYLES.font};`;
      btn.addEventListener('click', () => this.setCategory(cat));
      row.appendChild(btn);
    }
    return row;
  }

  private renderPatternGrid(): HTMLElement {
    const grid = document.createElement('div');
    grid.style.cssText = 'display:grid;grid-template-columns:1fr 1fr;gap:6px;max-height:320px;overflow-y:auto;';
    for (const pattern of this.getFilteredPatterns()) {
      grid.appendChild(this.renderPatternCard(pattern));
    }
    return grid;
  }

  private renderPatternCard(pattern: PatternData): HTMLElement {
    const card = document.createElement('div');
    card.style.cssText = `background:${STYLES.bgDark};border:1px solid ${STYLES.border};border-radius:4px;padding:6px;cursor:pointer;position:relative;`;
    card.addEventListener('mouseenter', () => { card.style.borderColor = STYLES.accent; delBtn.style.display = 'block'; });
    card.addEventListener('mouseleave', () => { card.style.borderColor = STYLES.border; delBtn.style.display = 'none'; });
    card.addEventListener('click', () => this.onSelect?.(pattern));

    const canvas = this.renderThumbnail(pattern);
    canvas.style.cssText = 'display:block;margin:0 auto 4px;border-radius:2px;';
    card.appendChild(canvas);

    const name = document.createElement('div');
    name.textContent = pattern.name;
    name.style.cssText = `font-size:11px;color:${STYLES.text};white-space:nowrap;overflow:hidden;text-overflow:ellipsis;`;
    card.appendChild(name);

    const badge = document.createElement('span');
    badge.textContent = pattern.category;
    badge.style.cssText = `display:inline-block;font-size:10px;padding:1px 4px;border-radius:3px;background:${STYLES.border};color:${STYLES.muted};margin-top:2px;`;
    card.appendChild(badge);

    const delBtn = document.createElement('button');
    delBtn.textContent = '\u00d7';
    delBtn.style.cssText = `display:none;position:absolute;top:2px;right:2px;background:none;border:none;color:${STYLES.muted};cursor:pointer;font-size:14px;line-height:1;`;
    delBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      if (pattern.id) this.deletePattern(pattern.id);
    });
    card.appendChild(delBtn);

    return card;
  }

  private renderThumbnail(pattern: PatternData, size = 64): HTMLCanvasElement {
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');
    if (!ctx || !pattern.triangles.length) return canvas;

    // Use the same centered IVM math as TrinityEngine + exportSVG so previews
    // match what the editor and exported assets show.
    const tris = pattern.triangles;
    const side = 10;
    const h = (side * SQRT3) / 2;

    // Compute every vertex first, then fit the full vertex bbox into the
    // canvas — bboxing centers leaves triangles overflowing the frame.
    const triVerts = tris.map((t) => {
      const center = toPixelCentered({ a: t.a, b: t.b, c: t.c }, side);
      const isUp = t.a + t.b + t.c === 2;
      const v: [number, number][] = isUp
        ? [
            [center.x, center.y - (2 * h) / 3],
            [center.x - side / 2, center.y + h / 3],
            [center.x + side / 2, center.y + h / 3],
          ]
        : [
            [center.x, center.y + (2 * h) / 3],
            [center.x - side / 2, center.y - h / 3],
            [center.x + side / 2, center.y - h / 3],
          ];
      return { vertices: v, color: t.color };
    });

    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const { vertices } of triVerts) {
      for (const [x, y] of vertices) {
        if (x < minX) minX = x;
        if (y < minY) minY = y;
        if (x > maxX) maxX = x;
        if (y > maxY) maxY = y;
      }
    }

    const pad = 4;
    const rangeX = maxX - minX || 1;
    const rangeY = maxY - minY || 1;
    const scale = Math.min((size - pad * 2) / rangeX, (size - pad * 2) / rangeY);
    const offX = (size - rangeX * scale) / 2 - minX * scale;
    const offY = (size - rangeY * scale) / 2 - minY * scale;

    for (const { vertices, color } of triVerts) {
      ctx.beginPath();
      ctx.moveTo(vertices[0][0] * scale + offX, vertices[0][1] * scale + offY);
      ctx.lineTo(vertices[1][0] * scale + offX, vertices[1][1] * scale + offY);
      ctx.lineTo(vertices[2][0] * scale + offX, vertices[2][1] * scale + offY);
      ctx.closePath();
      ctx.fillStyle = color || STYLES.accent;
      ctx.fill();
    }
    return canvas;
  }

  private renderFunctionEditor(): HTMLElement {
    const wrap = document.createElement('div');
    wrap.style.cssText = `border-top:1px solid ${STYLES.border};padding-top:8px;margin-top:4px;`;
    const label = document.createElement('div');
    label.textContent = 'Pattern Function';
    label.style.cssText = `font-size:11px;color:${STYLES.muted};margin-bottom:4px;`;
    const textarea = document.createElement('textarea');
    textarea.placeholder = '(a,b,c) => ...';
    textarea.style.cssText = `width:100%;height:48px;background:${STYLES.bgDark};color:${STYLES.text};border:1px solid ${STYLES.border};border-radius:4px;padding:4px;font-family:monospace;font-size:11px;resize:vertical;box-sizing:border-box;`;
    const btn = document.createElement('button');
    btn.textContent = 'Apply';
    btn.style.cssText = `margin-top:4px;padding:3px 12px;background:${STYLES.accent};color:#fff;border:none;border-radius:4px;cursor:pointer;font-size:11px;font-family:${STYLES.font};`;
    btn.addEventListener('click', () => {
      if (textarea.value.trim()) this.onApplyFunction?.(textarea.value.trim());
    });
    wrap.append(label, textarea, btn);
    return wrap;
  }

  private renderEmptyState(): HTMLElement {
    const div = document.createElement('div');
    div.textContent = 'No patterns found. Click refresh to load from server.';
    div.style.cssText = `color:${STYLES.muted};font-size:11px;text-align:center;padding:24px 8px;`;
    return div;
  }
}
